import {addIdToMap, getCustomId} from '../utils'
import type {DeserializeComponentParams} from '../types'
import type {DataModelAPI, DataModelExtensionAPI} from '../../../dataModel/dataModel'
import type {
    DeserializationMappers,
    InnerElement,
    Language,
    SerializedCompStructure,
    SerializedInnerElement,
    SerializedInnerElementsMap
} from '@wix/document-services-types'
import {DATA_TYPES, INNER_ELEMENT_TYPE} from '../../../../constants/constants'
import {updateComponentStylesStructure} from './style'
import {generateUniqueIdByType} from '../../../../utils/dataUtils'
import {addHash} from '../../../../utils/refArrayUtils'
import {updateComponentScopedValues} from './scopedValues'
import {intersection, mapValues, isEmpty} from 'lodash'
import type {ComponentsExtensionAPI} from '../../components'
import {updateComponentBindingsStructure} from './bindings'
import {updateComponentRuntimeBindingsStructure} from './runtimeBindings'

export function deserializeInnerElements(deserializeComponentParams: DeserializeComponentParams) {
    const {compStructure, customId, mappers, createExtArgs} = deserializeComponentParams
    const oldToNewIdMap = mappers?.oldToNewIdMap ?? {}
    deserializeComponentParams.customId = getCustomId(oldToNewIdMap, compStructure.innerElements, customId)
    const {dataModel} = createExtArgs.extensionAPI as DataModelExtensionAPI

    const {innerElements: innerElementsMap} = compStructure
    const innerElementsQuery = deserializeInnerElementsMap(innerElementsMap, dataModel, deserializeComponentParams)

    if (innerElementsQuery) {
        compStructure.innerElementsQuery = innerElementsQuery
        addIdToMap(compStructure.innerElements, innerElementsQuery, oldToNewIdMap)
    }
    delete compStructure.innerElements
}

function deserializeInnerElementsMap(
    innerElementsMap: SerializedInnerElementsMap | undefined,
    dataModel: DataModelAPI,
    deserializeComponentParams: DeserializeComponentParams
): string | undefined {
    if (!innerElementsMap) return

    const {pageId, mappers} = deserializeComponentParams
    const oldToNewIdMap = mappers?.oldToNewIdMap ?? {}

    if (innerElementsMap[DATA_TYPES.innerElements]) {
        const innerElements = Object.entries(innerElementsMap[DATA_TYPES.innerElements]).filter(
            ([, value]) => (value as InnerElement).type === INNER_ELEMENT_TYPE
        )
        innerElements.forEach(([id, innerElement]) => {
            innerElementsMap[DATA_TYPES.innerElements][id] = addHash(
                deserializeInnerElement(innerElement as SerializedInnerElement, dataModel, deserializeComponentParams)
            )
        })
    }

    return dataModel.addItem(innerElementsMap, DATA_TYPES.innerElements, pageId, undefined, '', oldToNewIdMap)?.id
}

function deserializeInnerElement(
    innerElement: SerializedInnerElement,
    dataModel: DataModelAPI,
    deserializeComponentParams: DeserializeComponentParams
): string {
    const {pageId, mappers, createExtArgs} = deserializeComponentParams
    const oldToNewIdMap = mappers?.oldToNewIdMap ?? {}

    innerElement.id = generateUniqueIdByType(
        DATA_TYPES.innerElements,
        pageId,
        createExtArgs.dal,
        createExtArgs.pointers
    )

    const deserializeComponentParamsForInnerElement = {
        ...deserializeComponentParams,
        compStructure: innerElement as SerializedCompStructure
    }

    deserializeInnerElementData(
        innerElement,
        dataModel,
        pageId,
        oldToNewIdMap,
        deserializeComponentParamsForInnerElement
    )
    deserializeInnerElementStyle(innerElement, deserializeComponentParamsForInnerElement)
    deserializeInnerElementSlots(innerElement, deserializeComponentParamsForInnerElement)
    deserializeInnerElementPresets(innerElement, deserializeComponentParamsForInnerElement)
    deserializeInnerElementBindings(innerElement, deserializeComponentParamsForInnerElement)
    deserializeInnerElementRuntimeBindings(innerElement, deserializeComponentParamsForInnerElement)
    deserializeInnerElements(deserializeComponentParamsForInnerElement)

    return dataModel.addItem(innerElement, DATA_TYPES.innerElements, pageId, innerElement.id, '', oldToNewIdMap)?.id
}

function deserializeInnerElementData(
    innerElement: SerializedInnerElement,
    dataModel: DataModelAPI,
    pageId: string,
    oldToNewIdMap: DeserializationMappers['oldToNewIdMap'],
    deserializeComponentParams: DeserializeComponentParams
) {
    if (!innerElement.data) {
        return
    }

    const dataId = dataModel.addItem(innerElement.data, DATA_TYPES.data, pageId, undefined, '', oldToNewIdMap, {
        elementType: innerElement.referredType
    }).id
    delete innerElement.data

    if (innerElement.translations) {
        const {dal, pointers} = deserializeComponentParams.createExtArgs
        const siteTranslations = Object.values(dal.get(pointers.multilingual.translationLanguages()) as Language[]).map(
            languageInfo => languageInfo.languageCode
        )
        const relevantLanguages = intersection(Object.keys(innerElement.translations), siteTranslations)

        relevantLanguages.forEach(languageCode => {
            dataModel.addItem(
                innerElement.translations[languageCode],
                DATA_TYPES.data,
                pageId,
                dataId,
                languageCode,
                oldToNewIdMap,
                {
                    elementType: innerElement.referredType
                }
            )
        })
        delete innerElement.translations
    }

    if (dataId) {
        innerElement.dataQuery = addHash(dataId)
    }
}

function deserializeInnerElementStyle(
    innerElement: SerializedInnerElement,
    deserializeComponentParams: DeserializeComponentParams
) {
    if (!innerElement.style && !innerElement.scopedStyles) {
        return
    }

    updateComponentStylesStructure(deserializeComponentParams)
    delete innerElement.style
    delete innerElement.scopedStyles
}

function deserializeInnerElementPresets(
    innerElement: SerializedInnerElement,
    deserializeComponentParams: DeserializeComponentParams
) {
    if (!innerElement.presets && !innerElement.scopedPresets) {
        return
    }

    const {createExtArgs, compStructure, pageId, mappers} = deserializeComponentParams
    updateComponentScopedValues(createExtArgs, compStructure, DATA_TYPES.presets, pageId, mappers)
    delete innerElement.presets
    delete innerElement.scopedPresets
}

function deserializeInnerElementSlots(
    innerElement: SerializedInnerElement,
    deserializeComponentParams: DeserializeComponentParams
) {
    const {createExtArgs, pageId, mappers} = deserializeComponentParams
    const {components} = createExtArgs.extensionAPI as ComponentsExtensionAPI
    const {dataModel} = createExtArgs.extensionAPI as DataModelExtensionAPI

    if (!innerElement?.slots) {
        return
    }
    const {type, slots: slotComponents} = innerElement.slots

    if (!mappers?.slotChildren || isEmpty(mappers.slotChildren)) {
        mappers!.slotChildren = {}
    }

    const addedSlotComponents = mapValues(slotComponents, component => {
        const componentPointer = components.getComponentToAddRef()
        mappers!.slotChildren![componentPointer.id] = component
        return componentPointer.id
    })

    const slotsData: any = {slots: {...addedSlotComponents}, type}
    const slotsDataPointer = dataModel.addItem(slotsData, DATA_TYPES.slots, pageId)
    delete innerElement.slots
    innerElement.slotsQuery = slotsDataPointer.id
}

function deserializeInnerElementBindings(
    innerElement: SerializedInnerElement,
    deserializeComponentParams: DeserializeComponentParams
) {
    if (!innerElement.bindings) {
        return
    }

    updateComponentBindingsStructure(deserializeComponentParams)
    delete innerElement.bindings
}

function deserializeInnerElementRuntimeBindings(
    innerElement: SerializedInnerElement,
    deserializeComponentParams: DeserializeComponentParams
) {
    if (!innerElement.runtimeBindings) {
        return
    }

    updateComponentRuntimeBindingsStructure(deserializeComponentParams)
    delete innerElement.runtimeBindings
}
